// VISITE GUIDÉE de l'interface d'un projet.
//
// Zygosight fait beaucoup de choses : la visite les présente DANS L'ORDRE où on
// s'en sert — la mesure zéro d'abord, puis les études qui expliquent les chiffres, puis
// ce qu'on en tire. Chaque étape met en lumière une partie de l'écran (repérée
// par un attribut `data-tour`) et dit, en deux phrases, à quoi elle sert.
//
// Elle s'ouvre seule la première fois qu'on entre dans un projet, et se relance
// à tout moment par le bouton « ? » du menu. « J'ai compris » est mémorisé si
// le visiteur a accepté les préférences d'affichage (bandeau cookies), sinon
// pour la durée de l'onglet seulement.
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Button } from '../components/ui';
import { savePreference } from '../legal/cookieConsent';
import './tour.css';

interface Step {
  /** Valeur de l'attribut `data-tour` de l'élément à montrer ; aucune = carte centrée. */
  target?: string;
  title: string;
  text: string;
}

export const TOUR_STEPS: Step[] = [
  {
    title: 'Bienvenue dans Zygosight',
    text: 'Tout part de la mesure : on suit l’adoption, on comprend pourquoi elle bouge avec des études, puis on décide et on vérifie l’effet. Cette visite vous montre l’interface en une minute.',
  },
  {
    target: 'todo',
    title: '1. La mesure zéro',
    text: 'Commencez ici : l’état des lieux du produit, ses utilisateurs et ses piliers d’adoption. L’ampoule indique ce qu’il reste à définir.',
  },
  {
    target: 'nav-etudes',
    title: '2. Les études',
    text: 'Pour expliquer un chiffre : créez une étude (entretiens, observation, test, questionnaire, audit). Chaque étude a son objectif et son protocole.',
  },
  {
    target: 'nav-entretiens',
    title: 'Entretiens et tests',
    text: 'Planifiez les séances, invitez les participants, importez enregistrements et transcriptions, puis découpez-les en verbatims.',
  },
  {
    target: 'nav-personas',
    title: 'Utilisateurs et personas',
    text: 'L’annuaire des vraies personnes, où vous piochez vos participants, et les personas, les profils types qui en ressortent.',
  },
  {
    target: 'nav-atelier',
    title: 'L’atelier',
    text: 'Un plan de travail façon post-it : regroupez les verbatims à la main. L’assistant IA propose des regroupements, il ne range rien à votre place.',
  },
  {
    target: 'nav-insights',
    title: 'Les insights',
    text: 'Les conclusions, toujours adossées à leurs preuves, avec une priorité expliquée, une hypothèse à tester et une métrique de succès.',
  },
  {
    target: 'nav-triangulation',
    title: 'La triangulation',
    text: 'Confrontez ce que disent les utilisateurs aux chiffres d’usage remontés par vos outils (PostHog, GA4, Matomo, Jira).',
  },
  {
    target: 'nav-dashboard',
    title: 'Le tableau de bord',
    text: 'Le score d’adoption, les objectifs, les mesures produit et leur évolution : l’impact de vos décisions, d’un coup d’œil.',
  },
  {
    target: 'nav-studio',
    title: 'Le studio de restitution',
    text: 'Assemblez un rapport à partager : synthèse, insights, personas, tests, décisions. Il se met à jour tout seul.',
  },
  {
    target: 'nav-copilote',
    title: 'Le copilote',
    text: 'Posez une question en langage naturel : il cherche dans toute la recherche du projet et cite ses sources.',
  },
  {
    target: 'options',
    title: 'Les options du projet',
    text: 'Objectifs business, taxonomie, équipe et connecteurs de mesure se règlent ici.',
  },
  {
    target: 'tour',
    title: 'C’est parti',
    text: 'Le projet exemple est là pour essayer sans risque. Relancez cette visite à tout moment avec ce bouton.',
  },
];

const DONE_KEY = 'zygo.tour.done';
let doneThisTab = false;

/** La visite a-t-elle déjà été vue ? */
export function tourAlreadySeen(): boolean {
  if (doneThisTab) return true;
  try {
    return localStorage.getItem(DONE_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen() {
  doneThisTab = true;
  savePreference(DONE_KEY, '1');
}

interface Box { top: number; left: number; width: number; height: number }

/** Le premier élément visé qui soit VISIBLE : certains existent en double, un
 *  pour l'ordinateur et un pour le téléphone (l'ampoule, par exemple). */
function findTarget(target?: string): HTMLElement | null {
  if (!target) return null;
  return [...document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`)]
    .find((el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden') ?? null;
}

/** Rectangle visible de l'élément visé, ou `null` s'il est absent ou masqué. */
function locate(target?: string): Box | null {
  const el = findTarget(target);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

const PAD = 6;
const CARD_W = 340;

export function GuidedTour({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [index, setIndex] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const step = TOUR_STEPS[index]!;
  const last = index === TOUR_STEPS.length - 1;

  const close = useCallback(() => {
    markSeen();
    setIndex(0);
    onClose();
  }, [onClose]);

  // Repérer l'élément visé, le faire défiler à l'écran, et suivre les changements de taille.
  useLayoutEffect(() => {
    if (!open) return undefined;
    findTarget(step.target)?.scrollIntoView({ block: 'nearest' });
    const update = () => setBox(locate(step.target));
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, step.target]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowRight' && !last) setIndex((i) => i + 1);
      else if (e.key === 'ArrowLeft' && index > 0) setIndex((i) => i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close, last, index]);

  if (!open) return null;

  // La carte se place à droite de l'élément s'il y a la place, sinon dessous ;
  // sans élément (ou sur petit écran), au centre.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let cardStyle: React.CSSProperties;
  if (box && vw > 640) {
    const right = box.left + box.width + 16;
    const left = right + CARD_W < vw ? right : Math.max(16, Math.min(box.left, vw - CARD_W - 16));
    const top = right + CARD_W < vw ? box.top : box.top + box.height + 12;
    cardStyle = { left, top: Math.max(16, Math.min(top, vh - 260)), width: CARD_W };
  } else {
    cardStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: Math.min(CARD_W, vw - 32) };
  }

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-labelledby="tour-title">
      {box ? (
        <div
          className="tour-spot"
          style={{ top: box.top - PAD, left: box.left - PAD, width: box.width + PAD * 2, height: box.height + PAD * 2 }}
          aria-hidden="true"
        />
      ) : (
        <div className="tour-veil" aria-hidden="true" />
      )}
      <div className="tour-card" style={cardStyle}>
        <span className="tour-count t-mono">{index + 1} / {TOUR_STEPS.length}</span>
        <h2 className="t-h3" id="tour-title">{step.title}</h2>
        <p className="tour-text">{step.text}</p>
        <div className="tour-actions">
          <Button variant="link" size="s" type="button" onClick={close}>Passer la visite</Button>
          <span className="tour-nav">
            {index > 0 ? <Button variant="ghost" onClick={() => setIndex((i) => i - 1)}>Précédent</Button> : null}
            <Button variant="accent" onClick={() => (last ? close() : setIndex((i) => i + 1))}>{last ? 'J’ai compris' : 'Suivant'}</Button>
          </span>
        </div>
      </div>
    </div>
  );
}
